(() => {
  const CARD_SELECTOR = "secondary-media.product-card__carousel";
  const DESKTOP_QUERY = "(min-width: 768px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  const preloadUrls = new Set();
  const pendingImages = new Set();

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  function findTemplate(carousel) {
    const sibling = carousel.previousElementSibling;
    if (sibling?.tagName === "TEMPLATE") return sibling;
    return carousel.parentElement?.querySelector(":scope > template") || null;
  }

  function preloadTemplate(carousel) {
    if (!isDesktop() || carousel.dataset.taymiImagesPreloaded === "true") return;

    const template = findTemplate(carousel);
    if (!template) return;

    carousel.dataset.taymiImagesPreloaded = "true";
    template.content.querySelectorAll("img").forEach((source) => {
      const src = source.getAttribute("src");
      const srcset = source.getAttribute("srcset");
      const cacheKey = srcset || src;
      if (!cacheKey || preloadUrls.has(cacheKey)) return;

      preloadUrls.add(cacheKey);
      const image = new Image();
      pendingImages.add(image);
      image.decoding = "async";
      const release = () => pendingImages.delete(image);
      image.addEventListener("load", release, { once: true });
      image.addEventListener("error", release, { once: true });
      if (source.getAttribute("sizes")) image.sizes = source.getAttribute("sizes");
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
    });
  }

  function tuneCarousel(carousel) {
    if (
      carousel.dataset.taymiCarouselTuned === "true" ||
      window.matchMedia(REDUCED_MOTION_QUERY).matches ||
      !carousel.carousel?.options
    ) {
      return;
    }

    carousel.carousel.options.selectedAttraction = 0.2;
    carousel.carousel.options.friction = 0.8;
    carousel.dataset.taymiCarouselTuned = "true";
  }

  const proximityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        preloadTemplate(entry.target);
        proximityObserver.unobserve(entry.target);
      });
    },
    { rootMargin: "800px 0px" }
  );

  function setupCarousel(carousel) {
    if (!(carousel instanceof HTMLElement)) return;

    if (carousel.dataset.taymiCarouselSetup !== "true") {
      carousel.dataset.taymiCarouselSetup = "true";
      proximityObserver.observe(carousel);
      carousel.addEventListener("pointerenter", () => {
        preloadTemplate(carousel);
        requestAnimationFrame(() => tuneCarousel(carousel));
      });
    }

    tuneCarousel(carousel);
  }

  function scan(root = document) {
    if (!isDesktop()) return;
    if (root.matches?.(CARD_SELECTOR)) setupCarousel(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach(setupCarousel);
  }

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.target.matches?.(CARD_SELECTOR)) tuneCarousel(record.target);
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        scan(node);
        const owner = node.closest?.(CARD_SELECTOR);
        if (owner) tuneCarousel(owner);
      });
    });
  });

  function start() {
    scan();
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  document.addEventListener("collection:reloaded", () => scan());
})();
